import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de subtipos de inmueble (type-property). Sexto
 * catálogo del backoffice en v2: un subtipo pertenece a un proyecto (referencia
 * `prj_`, el mismo molde de `$lookup` que estrenaron agencias/sociedades/proyectos)
 * y clasifica los inmuebles de ese proyecto.
 *
 * A diferencia del `code` GLOBAL de proyectos, el `code` del subtipo es único POR
 * PROYECTO: dos proyectos pueden tener cada uno su subtipo "APT". Por eso la
 * referencia al proyecto viaja SIEMPRE en la fila (no sólo el nombre): el `code`
 * solo desambigua junto a su proyecto.
 */

/**
 * Referencia al proyecto dueño, wire-safe: el `_id` no cruza el adapter, así que se
 * publica su `prj_` junto al nombre y el código (el `code` del proyecto es el
 * prefijo legible que el admin reconoce). Nullable por robustez del listado —igual
 * que la autoría—: una fila cuyo proyecto perdió el `publicId` presenta `null` en
 * vez de tumbar la página entera; `project` es required en el dominio, así que en
 * la práctica siempre se resuelve.
 */
export const V2AdminTypePropertyProjectSchema = z
  .object({
    id: PublicIdSchemas.project,
    name: z.string(),
    code: z.string()
  })
  .nullable()

/** Autoría wire-safe: `usr_`, nunca el ObjectId. */
export const V2AdminTypePropertyUserRefSchema = z.object({
  id: PublicIdSchemas.user,
  fullname: z.string()
})

/** La autoría puede faltar (usuario sin `publicId` o campo nulo). */
export const V2AdminTypePropertyAuthorSchema = V2AdminTypePropertyUserRefSchema.nullable()

/**
 * Los ATRIBUTOS del plano: cuántas habitaciones, baños y parqueos tiene.
 *
 * ── POR QUÉ VIVEN EN EL SUBTIPO Y NO EN EL INMUEBLE ──────────────────────────
 *
 * Un subtipo ES un plano, y un plano tiene una cantidad fija de habitaciones. Cargarlos
 * por inmueble sería capturar 382 veces lo que se define unas 50, y garantiza que dos
 * apartamentos del mismo plano terminen diciendo cosas distintas — que es peor que no
 * decir nada, porque el cliente no tiene cómo saber cuál de los dos miente.
 *
 * Todos NULLABLE, y no por prolijidad: hoy NINGUNO está cargado. Se capturan a mano desde
 * el back-office, así que el estado normal durante un buen rato va a ser «todavía no». La
 * app esconde el chip que no tiene dato en vez de dibujar un cero, que se leería como
 * «este departamento no tiene baños».
 */
export const V2TypePropertyAttributesSchema = z.object({
  bedrooms: z.number().int().nonnegative().nullable(),
  /**
   * DECIMAL, y en pasos de medio. «3.5 baños» no es un redondeo: un medio baño —inodoro y
   * lavamanos, sin ducha— es una categoría real y es lo que la maqueta dibuja. Un entero
   * obligaría a elegir entre mentir para arriba o para abajo.
   */
  bathrooms: z.number().nonnegative().multipleOf(0.5).nullable(),
  /** Los parqueos que le corresponden al plano, no los que el comprador contrató aparte. */
  parkingSpaces: z.number().int().nonnegative().nullable()
})

/** Fila del listado y del detalle. */
export const V2AdminTypePropertySchema = z.object({
  id: PublicIdSchemas.typeProperty,
  project: V2AdminTypePropertyProjectSchema,
  /** Único (case-insensitive) DENTRO del proyecto; se recorta al escribir. */
  code: z.string().min(1),
  /** `null` = sin nombre cargado (el subtipo puede existir sólo por su código). */
  name: z.string().nullable(),
  active: z.boolean(),
  ...V2TypePropertyAttributesSchema.shape,
  createdBy: V2AdminTypePropertyAuthorSchema,
  updatedBy: V2AdminTypePropertyAuthorSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminTypePropertiesPageSchema = z.object({
  data: z.array(V2AdminTypePropertySchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y faceta `active` con los mismos filtros allowlist del listado. La faceta
 * ignora el filtro de su propia dimensión; `total` respeta la consulta completa. El
 * proyecto no es una faceta: se filtra por nombre/código (texto).
 */
export const V2AdminTypePropertiesSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  })
})

/**
 * Entrada mínima del selector de subtipos. `project` (`prj_`) permite a los
 * formularios de inventario cascadear: primero se elige el proyecto y el selector de
 * subtipos se filtra por él en el cliente.
 */
export const V2AdminTypePropertyLookupItemSchema = z.object({
  id: PublicIdSchemas.typeProperty,
  code: z.string().min(1),
  name: z.string().nullable(),
  project: PublicIdSchemas.project
})

export const V2AdminTypePropertyLookupSchema = z.object({
  data: z.array(V2AdminTypePropertyLookupItemSchema)
})

/**
 * Alta de subtipo. El proyecto entra por `prj_` (no por ObjectId como el wire
 * legacy); el service lo resuelve y valida que exista. `code` se recorta y debe ser
 * único (case-insensitive) DENTRO del proyecto. `name` es opcional.
 */
export const V2CreateAdminTypePropertySchema = z.object({
  project: PublicIdSchemas.project,
  code: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  name: z
    .string()
    .transform((value) => value.trim())
    .optional(),
  active: z.boolean().optional(),
  /*
    Los atributos entran NULLABLE además de opcional, y la diferencia importa en un PATCH:
    omitir el campo es «no lo toques» y mandarlo en `null` es «bórralo». Sin la segunda,
    un dato cargado por error no se podría deshacer sin tocar la base.
  */
  bedrooms: z.number().int().nonnegative().nullable().optional(),
  bathrooms: z.number().nonnegative().multipleOf(0.5).nullable().optional(),
  parkingSpaces: z.number().int().nonnegative().nullable().optional()
})

/**
 * Edición parcial: un id desconocido responde 404 (nunca hace upsert). Mover el
 * subtipo a otro proyecto (`prj_`) o cambiar el `code` re-chequea la unicidad contra
 * el par code+proyecto EFECTIVO, excluyendo la propia fila.
 */
export const V2UpdateAdminTypePropertySchema = V2CreateAdminTypePropertySchema.partial()

export type V2TypePropertyAttributes = z.infer<typeof V2TypePropertyAttributesSchema>
export type V2AdminTypePropertyProject = z.infer<typeof V2AdminTypePropertyProjectSchema>
export type V2AdminTypePropertyUserRef = z.infer<typeof V2AdminTypePropertyUserRefSchema>
export type V2AdminTypePropertyAuthor = z.infer<typeof V2AdminTypePropertyAuthorSchema>
export type V2AdminTypeProperty = z.infer<typeof V2AdminTypePropertySchema>
export type V2AdminTypePropertiesPage = z.infer<typeof V2AdminTypePropertiesPageSchema>
export type V2AdminTypePropertiesSummary = z.infer<typeof V2AdminTypePropertiesSummarySchema>
export type V2AdminTypePropertyLookupItem = z.infer<typeof V2AdminTypePropertyLookupItemSchema>
export type V2AdminTypePropertyLookup = z.infer<typeof V2AdminTypePropertyLookupSchema>
export type V2CreateAdminTypePropertyInput = z.infer<typeof V2CreateAdminTypePropertySchema>
export type V2UpdateAdminTypePropertyInput = z.infer<typeof V2UpdateAdminTypePropertySchema>
